import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
const ROWS_PER_PAGE = 10;

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 900px) {
    border-radius: 1rem;
    padding: 1rem;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .7rem;
  margin: 1rem 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const Card = styled.div`border: 1px solid #dbeafe; background:#f8fbff; border-radius:.85rem; padding:.9rem;`;
const Label = styled.p`margin:0; color:#64748b; font-size:.82rem; font-weight:600;`;
const Value = styled.p`margin:.32rem 0 0; color:#1e3a8a; font-size:1.1rem; font-weight:800;`;
const Sub = styled.p`margin:.2rem 0 0; color:#64748b; font-size:.75rem;`;
const Section = styled.div`border:1px solid #e2e8f0; border-radius:.95rem; background:#fff; padding:1rem; margin-top:1rem;`;
const H3 = styled.h3`margin:0 0 .5rem; font-size:1rem; color:#0f172a;`;
const RankRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .65rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const RankBox = styled.div`border:1px solid #dbeafe; border-radius:.75rem; padding:.6rem .75rem; background:#eff6ff; color:#1d4ed8; font-weight:700; font-size:.86rem;`;
const SvgWrap = styled.div`position:relative; border:1px solid #e2e8f0; border-radius:.8rem; padding:.6rem; margin-top:.45rem;`;
const ChartTooltip = styled.div`
  position: absolute;
  left: ${({ $x }) => `${$x}%`};
  top: ${({ $y }) => `${$y}%`};
  transform: translate(-50%, -120%);
  background: rgba(15, 23, 42, 0.94);
  color: #f8fafc;
  border-radius: 0.45rem;
  padding: 0.35rem 0.5rem;
  font-size: 0.74rem;
  font-weight: 600;
  pointer-events: none;
  white-space: nowrap;
  z-index: 3;

  @media (max-width: 900px) {
    display: none;
  }
`;
const Legend = styled.div`display:flex; gap:.8rem; flex-wrap:wrap; font-size:.78rem; color:#475569; margin:.4rem 0 .2rem;`;
const Dot = styled.span`display:inline-block; width:.55rem; height:.55rem; border-radius:999px; background:${({ $color }) => $color}; margin-right:.3rem;`;
const TableWrap = styled.div`overflow:auto; border:1px solid #e2e8f0; border-radius:.85rem;`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 900px) {
    min-width: 640px;
  }
`;
const Th = styled.th`background:#f8fafc; color:#64748b; text-align:left; font-size:.8rem; font-weight:700; padding:.7rem;`;
const Tr = styled.tr``;
const Td = styled.td`
  border-top: 1px solid #f1f5f9;
  color: #1f2937;
  font-size: .88rem;
  padding: .7rem;
  background: ${({ $hl }) => ($hl ? '#eff6ff' : 'transparent')};
  border-left: ${({ $hl }) => ($hl ? '3px solid #2563eb' : '3px solid transparent')};
  font-weight: ${({ $hl }) => ($hl ? 700 : 500)};
`;
const Pager = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: .6rem;
  margin-top: .6rem;

  @media (max-width: 900px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;
const Btn = styled.button`border:1px solid #cbd5e1; background:#fff; border-radius:.6rem; padding:.35rem .6rem; cursor:pointer;`;
const Empty = styled.p`margin:0; padding:1rem; color:#64748b; text-align:center;`;
const Error = styled.p`margin:.6rem 0 0; color:#b91c1c; font-size:.9rem;`;
const ReasonWrap = styled.div`display:flex; flex-wrap:wrap; gap:.35rem; margin-top:.2rem;`;
const ReasonBadge = styled.span`
  display:inline-flex;
  align-items:center;
  border-radius:999px;
  padding:.1rem .45rem;
  font-size:.68rem;
  font-weight:800;
  color:${({ $type }) => ($type === 'speed' ? '#1d4ed8' : $type === 'accuracy' ? '#047857' : '#9a3412')};
  background:${({ $type }) => ($type === 'speed' ? '#dbeafe' : $type === 'accuracy' ? '#d1fae5' : '#ffedd5')};
`;
const HighlightGuide = styled.div`display:flex; flex-wrap:wrap; gap:.45rem; margin:.1rem 0 .6rem;`;
const ChartTabs = styled.div`display:flex; flex-wrap:wrap; gap:.5rem; margin-bottom:.5rem;`;
const ChartTabButton = styled.button`
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#cbd5e1')};
  background: ${({ $active }) => ($active ? '#eff6ff' : '#fff')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#475569')};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: center;
  }
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch { return null; }
};
const toDate = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
const formatDate = (v) => (toDate(v) ? toDate(v).toLocaleDateString('ko-KR') : '-');
const fmtType = (v) => (v === 'position' ? '자리연습' : v === 'sentence' ? '단문연습' : '낱말연습');
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const LineChart = ({ records, metrics }) => {
  const [tooltip, setTooltip] = useState(null);
  if (!records.length) return <Empty>최근 1개월 데이터가 없습니다.</Empty>;
  const width = 680;
  const height = 180;
  const pad = 24;
  const makePoints = (values) => {
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const gap = max - min || 1;
    return values.map((val, idx) => {
      const x = pad + (idx * (width - pad * 2)) / Math.max(values.length - 1, 1);
      const y = height - pad - ((val - min) / gap) * (height - pad * 2);
      return `${x},${y}`;
    }).join(' ');
  };
  return (
    <SvgWrap>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="180" role="img" aria-label="line chart">
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#cbd5e1" strokeWidth="1" />
        {metrics.map((m) => {
          const vals = records.map((r) => Number(r[m.key] || 0));
          const points = vals.map((val, idx) => {
            const max = Math.max(...vals, 1);
            const min = Math.min(...vals, 0);
            const gap = max - min || 1;
            const x = pad + (idx * (width - pad * 2)) / Math.max(vals.length - 1, 1);
            const y = height - pad - ((val - min) / gap) * (height - pad * 2);
            return { x, y, val, date: records[idx]?.date };
          });
          return (
            <g key={m.key}>
              <polyline fill="none" stroke={m.color} strokeWidth="2.2" points={makePoints(vals)} />
              <polyline
                fill="none"
                stroke="transparent"
                strokeWidth="14"
                points={makePoints(vals)}
                onMouseMove={(event) => {
                  const rect = event.currentTarget.ownerSVGElement.getBoundingClientRect();
                  const xInView = ((event.clientX - rect.left) / rect.width) * width;
                  const idx = clamp(
                    Math.round(((xInView - pad) / Math.max(width - pad * 2, 1)) * Math.max(vals.length - 1, 0)),
                    0,
                    vals.length - 1
                  );
                  const point = points[idx];
                  if (!point) return;
                  setTooltip({
                    x: (point.x / width) * 100,
                    y: (point.y / height) * 100,
                    text: `${formatDate(point.date)} | ${m.label}: ${point.val}`
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
              />
              {points.map((p, i) => (
                <circle
                  key={`${m.key}-${i}`}
                  cx={p.x}
                  cy={p.y}
                  r="4"
                  fill={m.color}
                  onMouseEnter={() => setTooltip({
                    x: (p.x / width) * 100,
                    y: (p.y / height) * 100,
                    text: `${formatDate(p.date)} | ${m.label}: ${p.val}`
                  })}
                  onMouseMove={() => setTooltip({
                    x: (p.x / width) * 100,
                    y: (p.y / height) * 100,
                    text: `${formatDate(p.date)} | ${m.label}: ${p.val}`
                  })}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
            </g>
          );
        })}
      </svg>
      {tooltip && <ChartTooltip $x={tooltip.x} $y={tooltip.y}>{tooltip.text}</ChartTooltip>}
      <Legend>
        {metrics.map((m) => <span key={`lg-${m.key}`}><Dot $color={m.color} />{m.label}</span>)}
      </Legend>
    </SvgWrap>
  );
};

const PortalTypingRecordsPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [activeChartKey, setActiveChartKey] = useState('');

  useEffect(() => {
    const run = async () => {
      if (!portalUser?.login_id) return;
      try {
        const data = await request({ endpoint: `${BACKEND_BASE_URL}/api/typing/student/records?login_id=${encodeURIComponent(portalUser.login_id)}` });
        setRecords(data?.records || []);
        setSummary(data?.summary || {});
      } catch (e) { setError(e?.responseData?.message || '타자 기록을 불러오지 못했습니다.'); }
    };
    run();
  }, [portalUser?.login_id, request]);

  const oneMonthAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  }, []);
  const monthRecords = useMemo(() => records.filter((r) => {
    const d = toDate(r.date);
    return d && d >= oneMonthAgo;
  }).sort((a, b) => new Date(a.date) - new Date(b.date)), [records, oneMonthAgo]);

  const grouped = useMemo(() => ({
    position: monthRecords.filter((r) => r.practice_type === 'position'),
    sentence: monthRecords.filter((r) => r.practice_type === 'sentence'),
    word: monthRecords.filter((r) => r.practice_type === 'word'),
  }), [monthRecords]);
  const chartSlides = useMemo(() => ([
    {
      key: 'position',
      title: '자리연습 (최근 1개월)',
      records: grouped.position,
      metrics: [
        { key: 'accuracy', label: '정확도(%)', color: '#10b981' },
        { key: 'duration_seconds', label: '소요시간(초)', color: '#f59e0b' }
      ]
    },
    {
      key: 'sentence',
      title: '단문연습 (최근 1개월)',
      records: grouped.sentence,
      metrics: [
        { key: 'typing_speed', label: '타수', color: '#2563eb' },
        { key: 'accuracy', label: '정확도(%)', color: '#10b981' },
        { key: 'duration_seconds', label: '소요시간(초)', color: '#f59e0b' }
      ]
    },
    {
      key: 'word',
      title: '낱말연습 (최근 1개월)',
      records: grouped.word,
      metrics: [
        { key: 'typing_speed', label: '타수', color: '#2563eb' },
        { key: 'accuracy', label: '정확도(%)', color: '#10b981' },
        { key: 'duration_seconds', label: '소요시간(초)', color: '#f59e0b' }
      ]
    }
  ]), [grouped.position, grouped.sentence, grouped.word]);
  const visibleSlides = useMemo(
    () => chartSlides.filter((slide) => (slide.records || []).length > 0),
    [chartSlides]
  );
  useEffect(() => {
    if (!visibleSlides.length) {
      setActiveChartKey('');
      return;
    }
    const hasActive = visibleSlides.some((slide) => slide.key === activeChartKey);
    if (!hasActive) {
      setActiveChartKey(visibleSlides[0].key);
    }
  }, [activeChartKey, visibleSlides]);
  const activeSlide = useMemo(
    () => visibleSlides.find((slide) => slide.key === activeChartKey) || visibleSlides[0] || null,
    [activeChartKey, visibleSlides]
  );
  const activePracticeRanks = summary?.last_week_practice_ranks?.[activeSlide?.key] || {};

  const bestByType = useMemo(() => {
    const types = ['position', 'word', 'sentence'];
    const result = {};
    types.forEach((type) => {
      const typed = records.filter((record) => record.practice_type === type);
      result[type] = {
        speed: typed.reduce((best, record) => (!best || (record.typing_speed || 0) > (best.typing_speed || 0) ? record : best), null),
        accuracy: typed.reduce((best, record) => (!best || (record.accuracy || 0) > (best.accuracy || 0) ? record : best), null),
        time: typed.reduce((best, record) => (!best || (record.duration_seconds || 999999) < (best.duration_seconds || 999999) ? record : best), null),
      };
    });
    return result;
  }, [records]);

  const totalPages = Math.max(1, Math.ceil(records.length / ROWS_PER_PAGE));
  const paged = useMemo(() => records.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE), [records, page]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const getReasons = (record) => {
    const typeBest = bestByType[record.practice_type] || {};
    const reasons = [];
    const typeLabel = fmtType(record.practice_type);
    if (typeBest.speed?.id === record.id) reasons.push({ key: 'speed', label: `${typeLabel} 최고 타수` });
    if (typeBest.accuracy?.id === record.id) reasons.push({ key: 'accuracy', label: `${typeLabel} 최고 정확도` });
    if (typeBest.time?.id === record.id) reasons.push({ key: 'time', label: `${typeLabel} 최단 시간` });
    return reasons;
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="MY TYPING RECORDS"
        title="내 타자 기록"
        description="연습한 결과가 어떻게 쌓였는지 그래프와 인상 깊었던 기록으로 확인해 보세요."
      />

      <Grid>
        <Card><Label>전체 기록 수</Label><Value>{summary.total_records || 0}</Value></Card>
        <Card>
          <Label>최고 타수</Label><Value>{summary.best_speed || 0} 타/분</Value>
          <Sub>{summary.best_speed_date ? `${formatDate(summary.best_speed_date)} 기록` : '-'}</Sub>
        </Card>
        <Card>
          <Label>최고 정확도</Label><Value>{summary.best_accuracy || 0}%</Value>
          <Sub>{summary.best_accuracy_date ? `${formatDate(summary.best_accuracy_date)} 기록` : '-'}</Sub>
        </Card>
      </Grid>

      <Section>
        <H3>지난주 연습유형별 내 랭킹</H3>
        <RankRow>
          <RankBox>타수 순위: {activePracticeRanks?.speed ? `${activePracticeRanks.speed}위` : '데이터 없음'}</RankBox>
          <RankBox>정확도 순위: {activePracticeRanks?.accuracy ? `${activePracticeRanks.accuracy}위` : '데이터 없음'}</RankBox>
          <RankBox>시간 순위: {activePracticeRanks?.time ? `${activePracticeRanks.time}위` : '데이터 없음'}</RankBox>
        </RankRow>
      </Section>

      <Section>
        {visibleSlides.length > 0 ? (
          <>
            <ChartTabs>
              {visibleSlides.map((slide) => (
                <ChartTabButton
                  key={slide.key}
                  type="button"
                  $active={activeSlide?.key === slide.key}
                  onClick={() => setActiveChartKey(slide.key)}
                >
                  {slide.title.replace(' (최근 1개월)', '')}
                </ChartTabButton>
              ))}
            </ChartTabs>
            <H3>{activeSlide.title}</H3>
            <LineChart records={activeSlide.records} metrics={activeSlide.metrics} />
          </>
        ) : (
          <>
            <H3>최근 1개월 라인그래프</H3>
            <Empty>최근 1개월 데이터가 없습니다.</Empty>
          </>
        )}
      </Section>

      <Section>
        <H3>전체 기록</H3>
        <HighlightGuide>
          <ReasonBadge $type="speed">유형별 최고 타수</ReasonBadge>
          <ReasonBadge $type="accuracy">유형별 최고 정확도</ReasonBadge>
          <ReasonBadge $type="time">유형별 최단 시간</ReasonBadge>
        </HighlightGuide>
        <TableWrap>
          {paged.length ? (
            <Table>
              <thead><tr><Th>날짜</Th><Th>연습 유형</Th><Th>타수</Th><Th>정확도</Th><Th>소요시간</Th></tr></thead>
              <tbody>
                {paged.map((r) => (
                  <Tr key={r.id}>
                    {(() => {
                      const reasons = getReasons(r);
                      const isHighlighted = reasons.length > 0;
                      return (
                        <>
                          <Td $hl={isHighlighted}>
                            {formatDate(r.date)}
                            {isHighlighted && (
                              <ReasonWrap>
                                {reasons.map((reason) => (
                                  <ReasonBadge key={`${r.id}-${reason.key}`} $type={reason.key}>
                                    {reason.label}
                                  </ReasonBadge>
                                ))}
                              </ReasonWrap>
                            )}
                          </Td>
                          <Td $hl={isHighlighted}>{fmtType(r.practice_type)}</Td>
                          <Td $hl={reasons.some((reason) => reason.key === 'speed')}>{r.typing_speed || 0}</Td>
                          <Td $hl={reasons.some((reason) => reason.key === 'accuracy')}>{r.accuracy || 0}%</Td>
                          <Td $hl={reasons.some((reason) => reason.key === 'time')}>{r.duration_seconds || 0}초</Td>
                        </>
                      );
                    })()}
                  </Tr>
                ))}
              </tbody>
            </Table>
          ) : <Empty>{loading ? '불러오는 중...' : '기록이 없습니다.'}</Empty>}
        </TableWrap>
        <Pager>
          <Btn type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}>이전</Btn>
          <span>{page} / {totalPages}</span>
          <Btn type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>다음</Btn>
        </Pager>
      </Section>
      {error && <Error>{error}</Error>}
    </Wrapper>
  );
};

export default PortalTypingRecordsPage;
